import { COUNTRY_CODE } from "@dw/constants/intl";
import { IAuditBase, IDateOnlyRange, IMenuItem } from "./shared";
import { splitByUpperCase } from "@dw/utils";
import { Dayjs } from '@dw/dayjs'

export enum BILLING_FREQUENCY {
  /**
   * Bill once per school term
   */
  TERM = "Term"
}
export const BILLING_FREQUENCY_OPTIONS: IMenuItem[] = Object.values(BILLING_FREQUENCY).map(x => ({
  value: x,
  text: splitByUpperCase(x)
}));

/**
 * Whether a dance school pins the look of the Dance World app for its dancers, or lets each dancer
 * choose for themselves.
 *
 * A studio's brand does not always survive both grounds — a notice icon or a logo drawn for white
 * paper can disappear on a dark one, and the reverse. Pinning is how a school keeps the app looking
 * like the school.
 *
 * Mirrors the API's `DanceSchoolAppAppearance`, which serializes each value as its PascalCase name.
 */
export enum DANCE_SCHOOL_APP_APPEARANCE {
  /** Each dancer picks Light / Dark / Match device in the app. The default. */
  DANCER_CHOICE = 'DancerChoice',
  LIGHT = 'Light',
  DARK = 'Dark'
}

/**
 * Whether a dance school appears in the app's public "Find your dance school" directory, which is
 * where a dancer who is not yet enrolled anywhere goes looking for a studio.
 *
 * Hiding is scoped to DISCOVERY. Families already at the school are unaffected — the API still
 * serves an unlisted school to a caller associated with it, because the app reads their class
 * venues off the same record. It does not close enrolments either; that is Enrolment Open.
 *
 * Mirrors the API's `DanceSchoolDirectoryListing`, which serializes each value as its PascalCase name.
 */
export enum DANCE_SCHOOL_DIRECTORY_LISTING {
  /** Shown in the directory. The default, and what every school had before this existed. */
  LISTED = 'Listed',
  HIDDEN = 'Hidden'
}

export interface IDanceSchoolTerm {
  termNumber: number;
  termDates: IDateOnlyRange;
}

/**
 * A term within a dance school's annual settings, carrying a stable identity.
 * Returned sorted by startDate alongside the legacy `termDates` array.
 */
export interface IDanceSchoolAnnualTerm {
  id: string;
  startDate: Dayjs;
  endDate: Dayjs;
}

export interface IDanceSchool extends IAuditBase {
  id: string;
  name: string;
  /** The logo for invoices and documents. Also what the app falls back to when there is no app logo. */
  logoKey: string;
  /**
   * A logo drawn for the app, where an invoice logo is rarely the right shape — a letterhead
   * wordmark is illegible at 34px tall. Optional, and optional permanently: most studios will only
   * ever upload one.
   */
  appLogoKey?: string;
  /**
   * The app logo's pixel dimensions, measured here at upload and stored by the API so the app can
   * choose its header layout before the image loads. Absent for logos uploaded before this existed.
   */
  appLogoWidth?: number;
  appLogoHeight?: number;
  /**
   * The school's general mark — shown wherever there is nothing more specific. Optional: clients
   * fall back to `logoKey`, which is the one that gates the school's Active status.
   */
  schoolLogoKey?: string;
  /**
   * The mark drawn to be read inside a square — the app's directory rows and school picker, this
   * portal's header mark and school switcher. Optional: clients fall back through `schoolLogoKey`
   * to `logoKey`.
   */
  squareLogoKey?: string;
  /**
   * The appearance this school pins for the Dance World app, or `DancerChoice` when the dancer
   * decides. Optional only for older-API tolerance — treat an absent value as `DancerChoice`.
   */
  appAppearance?: DANCE_SCHOOL_APP_APPEARANCE;
  /**
   * Whether the school is listed in the app's dance school directory. Optional only for older-API
   * tolerance — treat an absent value as `Listed`.
   */
  directoryListing?: DANCE_SCHOOL_DIRECTORY_LISTING;
  countryCode: COUNTRY_CODE;
  annualSettings: IDanceSchoolAnnualSettings[];
  /**
   * The school's current enrolment year — explicit owner-controlled state (scalability Part 07).
   * Optional only for older-API tolerance; the current API always emits it.
   */
  currentEnrolmentYear?: number;
  xeroConnectionStatus: IDanceSchoolXeroConnectionStatus;
  termsAndConditions: IDanceSchoolTermsAndConditions[];
  billingSettings: IDanceSchoolBillingSettings;
  publicInfo: IDanceSchoolPublicInfo;
  /**
   * Branding details rendered onto first-party invoices and the public invoice page.
   * Additive field - may be absent on responses from older API versions.
   */
  invoiceBrandingSettings?: IInvoiceBrandingSettings;
}

export interface IInvoiceBrandingSettings {
  legalName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postalCode: string;
  countryCode: string;
  contactEmail: string;
  contactPhone: string;
  taxNumber: string;
  bankAccountName: string;
  bankAccountNumber: string;
  invoiceFooterNotes: string;
  /**
   * Brand colours applied to the public invoice page and first-party invoices.
   * Hex strings (e.g. "#008080"). Additive - may be absent on older API responses.
   */
  primaryColor?: string;
  accentColor?: string;
}

export interface IDanceSchoolPublicInfo {
  websiteUrl: string;
}

export interface IDanceSchoolBillingSettings {
  /**
   * How many days before the cycle starts should invoices be generated
   */
  daysBeforeCycleStartToGenerateInvoices: number;

  /**
   * Should invoices automatically be approved and sent out when generated
   */
  autoApproveAndSendInvoices: boolean;

  /**
   * How many days before a cycle starts should the invoice be due
   */
  invoicesDueDaysBeforeCycleStarts: number;
}

export interface IDanceSchoolAnnualSettings {
  year: number;
  billingFrequency: BILLING_FREQUENCY;
  /**
   * Legacy date-only twin of `terms` - prefer `terms` (stable ids). Portal consumers
   * move off this across scalability Parts 08/10; removed portal-side in Part 20.
   */
  termDates: IDateOnlyRange[];
  /**
   * Whether enrolments are open for this year. Explicit owner-controlled state introduced in
   * scalability Part 07 (previously derived from the combined start-of-year flag).
   */
  enrolmentOpen: boolean;
  /**
   * Terms with stable identities, sorted by startDate. Always present (the API emits
   * at least an empty array); empty until the server-side term-identity migration has run.
   */
  terms: IDanceSchoolAnnualTerm[];
}

export interface IDanceSchoolXeroConnectionStatus {
  connected: boolean;
  connectedSince?: Dayjs;
  xeroTenantName: string;
  xeroTenantId: string;
  xeroSalesAccountCode: string;
  /**
   * Xero bank/clearing account (AccountID) that recorded payments are posted to. Additive - may be
   * absent on responses from older API versions.
   */
  xeroPaymentAccountId: string;
}

export interface IDanceSchoolTermsAndConditions {
  fileKey: string;
  fileUploadName: string;
  isActive: boolean;
  createdTimestamp: Dayjs;
}

export interface ICreateDanceSchoolRequest {
  name: string;
  logoKey: string;
  countryCode: COUNTRY_CODE;
  websiteUrl: string;
}

/**
 * The mark to draw for a school wherever this portal shows it inside a SQUARE — the header mark, the
 * school switcher, an invite row.
 *
 * The one place the fallback is decided, so a caller reading `squareLogoKey` directly is never a
 * caller who has to remember the rest of the chain. Every school has `logoKey` (it is the one that
 * gates Active), so this only returns undefined for a school with no logo at all.
 *
 * Deliberately NOT the same chain the mobile app uses — the app has never carried `schoolLogoKey`.
 * Anything drawing a picture OF the app wants that shorter chain instead.
 */
export const getSchoolMarkKey = (school?: Pick<IDanceSchool, 'squareLogoKey' | 'schoolLogoKey' | 'logoKey'>): string | undefined =>
  school ? school.squareLogoKey || school.schoolLogoKey || school.logoKey || undefined : undefined;

export interface IUpdateDanceSchoolPublicInfoRequest {
  name: string;
  logoKey: string;
  websiteUrl: string;
  /**
   * Omit to leave the app logo exactly as it is — the API never clears it by omission, which is
   * what lets the other forms on this page save without knowing the field exists.
   */
  appLogoKey?: string;
  appLogoWidth?: number;
  appLogoHeight?: number;
  /** Explicitly drop the app logo and go back to using the invoice logo in the app. */
  removeAppLogo?: boolean;
  /**
   * Omit to leave the school logo exactly as it is — same contract as `appLogoKey`, so any other
   * form on the page can save without knowing this field exists.
   */
  schoolLogoKey?: string;
  /** Explicitly drop the school logo and fall back to the invoice logo. */
  removeSchoolLogo?: boolean;
  /**
   * Omit to leave the square logo exactly as it is — same contract as `appLogoKey`, so any other
   * form on the page can save without knowing this field exists.
   */
  squareLogoKey?: string;
  /** Explicitly drop the square logo and fall back down the chain. */
  removeSquareLogo?: boolean;
}

/**
 * Settings for the Dance World app itself — what a school's dancers see, as opposed to who the
 * school is (public info) or how it bills (billing settings).
 *
 * Every field is optional and means "leave it alone" when omitted, so this stays a partial update
 * as more app settings arrive.
 */
export interface IUpdateDanceSchoolAppSettingsRequest {
  appAppearance?: DANCE_SCHOOL_APP_APPEARANCE | null;
  directoryListing?: DANCE_SCHOOL_DIRECTORY_LISTING | null;
}

export interface IUpdateDanceSchoolTermsAndConditionsRequest {
  fileKey: string;
}

export interface IUpsertDanceSchoolAnnualTermRequest {
  /**
   * Omit to create a new term (the server assigns a GUID); supply an existing
   * term's id to preserve its identity. Unknown ids are rejected.
   */
  id?: string;
  startDate: string;
  endDate: string;
}

export interface IUpsertDanceSchoolAnnualSettingsRequest {
  year: number;
  billingFrequency: BILLING_FREQUENCY;
  /**
   * Identity-carrying terms. Required since scalability Part 10, which completed the retirement
   * of the legacy `termDates` request path (removed here together with the API's legacy twin).
   * The server preserves supplied term ids and mints ids for newly-added terms.
   */
  terms: IUpsertDanceSchoolAnnualTermRequest[];
}

export interface ISetCurrentEnrolmentYearRequest {
  year: number;
}

export interface IUpdateDanceSchoolXeroSalesAccountCodeRequest {
  xeroSalesAccountCode: string;
}

export interface IUpdateDanceSchoolXeroPaymentAccountRequest {
  xeroPaymentAccountId: string;
}

export interface IUpdateDanceSchoolBillingSettingsRequest {
  daysBeforeCycleStartToGenerateInvoices: number | null;
  autoApproveAndSendInvoices: boolean | null;
  invoicesDueDaysBeforeCycleStarts: number | null;
}

export interface IUpdateDanceSchoolInvoiceBrandingRequest {
  /**
   * All fields are optional. Omit or send `null` to leave a value unchanged;
   * send an empty string ("") to clear it.
   */
  legalName?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  taxNumber?: string | null;
  bankAccountName?: string | null;
  bankAccountNumber?: string | null;
  invoiceFooterNotes?: string | null;
  primaryColor?: string | null;
  accentColor?: string | null;
}

/**
 * scalability Part 10 - term-date change workflow.
 *
 * Advisory codes on a term-date change report. A warning never blocks the change; some require an
 * explicit acknowledgement before the change is applied (see `ITermDateChangeWarning.requiresAcknowledgement`).
 * The API serializes each code as its PascalCase name.
 */
export enum TERM_DATE_CHANGE_WARNING_CODE {
  BOUNDARY_CROSSES_INVOICED_CYCLE = "BoundaryCrossesInvoicedCycle",
  BOUNDARY_CROSSES_PAID_CYCLE = "BoundaryCrossesPaidCycle",
  ATTENDANCE_ROWS_OUTSIDE_WINDOWS = "AttendanceRowsOutsideWindows",
  TRIALS_OUTSIDE_WINDOWS = "TrialsOutsideWindows",
  EMAILS_ALREADY_SENT_WITH_OLD_DATES = "EmailsAlreadySentWithOldDates",
}

/**
 * Hard blockers on a term-date change report. A blocker prevents the change from being applied at all -
 * term structure (add/remove) cannot change while a year holds enrolments. Serialized as its PascalCase name.
 */
export enum TERM_DATE_CHANGE_BLOCKER_CODE {
  TERM_ADDED_WITH_ENROLMENTS = "TermAddedWithEnrolments",
  TERM_REMOVED_WITH_ENROLMENTS = "TermRemovedWithEnrolments",
  TERM_REMOVED_WITH_INVOICED_CYCLES = "TermRemovedWithInvoicedCycles",
  TERM_OVERLAPS_ANOTHER_YEAR = "TermOverlapsAnotherYear",
}

/** A matched term whose start or end date differs between the stored and requested configurations. */
export interface ITermDateChangeReportChangedTerm {
  termId: string;
  oldStartDate: Dayjs;
  oldEndDate: Dayjs;
  newStartDate: Dayjs;
  newEndDate: Dayjs;
}

/** Per-dance-class breakdown of an affected count on the report. */
export interface ITermDateChangeReportClassCount {
  danceClassId: string;
  danceClassName: string;
  count: number;
}

export interface ITermDateChangeWarning {
  code: TERM_DATE_CHANGE_WARNING_CODE;
  message: string;
  requiresAcknowledgement: boolean;
  affectedCount: number;
}

export interface ITermDateChangeBlocker {
  code: TERM_DATE_CHANGE_BLOCKER_CODE;
  message: string;
}

/**
 * Impact assessment for a proposed term-date change. Produced read-only by the change-preview endpoint
 * and re-produced server-side by the apply endpoint. Counts describe what the change would touch; warnings
 * are advisory (some need acknowledgement); blockers prevent the change entirely.
 */
export interface ITermDateChangeReport {
  year: number;
  /** The matched terms whose start or end date differs between the stored and requested configurations. */
  changedTerms: ITermDateChangeReportChangedTerm[];
  /** Count of not-yet-invoiced fee cycles (Active/Pending enrolments) on a changed term - these regenerate with the new dates. */
  uninvoicedFeeCyclesFollowing: number;
  /** The `uninvoicedFeeCyclesFollowing` total broken down per dance class. */
  uninvoicedFeeCyclesByClass: ITermDateChangeReportClassCount[];
  /** Count of draft (ApprovalPending) invoices with a line on a changed term - they can be regenerated. */
  approvalPendingInvoicesAffected: number;
  approvalPendingInvoiceIds: string[];
  /** Count of issued (AwaitingPayment/PartiallyPaid) invoices referencing a changed term - these stay frozen as invoiced. */
  issuedInvoicesUnchanged: number;
  issuedInvoiceIdsReferencingChangedTerms: string[];
  /** Count of enrolments whose start or end date would shift under the new terms. */
  enrolmentWindowsShifting: number;
  attendanceRowsOutsideWindows: number;
  trialsOutsideWindows: number;
  /** Distinct families that would need re-notifying (affected by a changed-term cycle or a shifting window). */
  familiesToNotify: number;
  warnings: ITermDateChangeWarning[];
  blockers: ITermDateChangeBlocker[];
  /** True when any warning requires an explicit acknowledgement before applying. */
  requiresAcknowledgement: boolean;
  /** True when any blocker is present - the change cannot be applied. */
  blocked: boolean;
}

/**
 * Read-only preview of a proposed term-date change. Sends the same edited term list that `apply` would send
 * and returns the impact assessment (`ITermDateChangeReport`) without any writes. The active dance school is
 * resolved server-side from the cookie, so it is not part of the body.
 */
export interface IPreviewTermDateChangeRequest {
  year: number;
  terms: IUpsertDanceSchoolAnnualTermRequest[];
}

/**
 * Applies a proposed term-date change. Same shape as the preview plus `acknowledgeWarnings`, which must be
 * `true` to proceed when the (server-recomputed) report carries warnings that require acknowledgement.
 */
export interface IApplyTermDateChangeRequest {
  year: number;
  terms: IUpsertDanceSchoolAnnualTermRequest[];
  acknowledgeWarnings: boolean;
}
